/**
 * Cart drawer: Dawn's <cart-drawer> and <cart-drawer-items>, rebuilt for the ATB redesign (2026-10-26).
 * Markup: snippets/cart-drawer.liquid. Styles: assets/atb-cart.css. Notes: CART_DRAWER_CUSTOMIZATIONS.md.
 *
 * The owner's rule: nothing in the drawer moves when a quantity changes, an item is added or removed, free shipping
 * unlocks or a discount appears. Dawn re-renders the whole drawer (innerHTML) on every change, which resets the
 * scroll and rebuilds every row. Here, while the drawer is open, the new server HTML is MORPHED into the page:
 *   - every changing value lives in a fixed slot ([data-cd-slot]) and only its contents are swapped;
 *   - rows are matched by line key; a new row slides in at the end of the list (height animates on that row only);
 *   - a removed row turns into "Removed X. Undo" at its own height for 5 seconds, then folds away;
 *   - removing the last item, or clearing the cart, fades to the empty view.
 * When the drawer is closed, a full re-render is invisible, so it still happens (fresh order, fresh suggestions).
 *
 * Kept for other code: renderContents(parsedState), getSectionsToRender(), setActiveElement(), open(), close(),
 * the is-empty class, and the ids in the snippet. Every add-to-cart path (product form, sticky bar, bundles, PDP
 * upsell, quick find, Open the Box, collection pages, ATB-OS favorites / landing / feed) goes through
 * renderContents() or replaces #CartDrawer; both are handled.
 */
const CD_UNDO_MS = 5000;
const CD_MOTION_MS = 280;
const cdReducedMotion = () => !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
const cdSectionsUrl = () => window.location.pathname;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // Delegated, so it survives #CartDrawer being replaced.
    this.addEventListener('click', (evt) => {
      if (evt.target && evt.target.id === 'CartDrawer-Overlay') this.close();
    });
    this.onTrapKeydown = this.onTrapKeydown.bind(this);
    this.setHeaderCartIconAccessibility();
    this._refreshing = null;
    this._refreshAgain = false;
    this._fading = false;
  }

  connectedCallback() {
    this.syncEmpty();
    this.refreshEta();
    // Other code replaces #CartDrawer (or .drawer__inner) wholesale: keep is-empty honest whatever the path.
    const root = this.querySelector('#CartDrawer');
    if (root && 'MutationObserver' in window && !this._observer) {
      this._observer = new MutationObserver(() => this.syncEmpty());
      this._observer.observe(root, { childList: true, subtree: true });
    }
  }

  /* is-empty follows the rows on the page. A row waiting for Undo still counts, so the cart view stays up. */
  syncEmpty() {
    if (this._fading) return;
    this.classList.toggle('is-empty', !this.querySelector('#CartDrawer .cd-line'));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.refreshEta();
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        if (typeof removeTrapFocus === 'function') removeTrapFocus();
        const inner = this.querySelector('.drawer__inner');
        if (inner && !this.contains(document.activeElement)) inner.focus({ preventScroll: true });
      },
      { once: true }
    );

    document.addEventListener('keydown', this.onTrapKeydown);
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    document.removeEventListener('keydown', this.onTrapKeydown);
    // Back to whatever opened the drawer; if that is unknown or gone (Safari doesn't focus clicked buttons),
    // the header cart icon, so focus never stays inside the hidden drawer.
    let back = this.activeElement;
    if (!back || back === document.body || !back.isConnected || this.contains(back)) back = document.querySelector('#cart-icon-bubble');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(back);
    else if (back) back.focus();
    document.body.classList.remove('overflow-hidden');
    const listfoot = this.querySelector('[data-cd-listfoot]');
    if (listfoot) listfoot.classList.remove('is-asking');
  }

  /* Focus trap that only counts what is visible (the hidden view's buttons are skipped). */
  onTrapKeydown(event) {
    if (event.key !== 'Tab' || !this.classList.contains('active')) return;
    const items = this.focusables();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (!this.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }
    if (event.shiftKey && (active === first || active === this.querySelector('.drawer__inner'))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  focusables() {
    const root = this.querySelector('#CartDrawer');
    if (!root) return [];
    const selector =
      'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex^="-"])';
    return Array.from(root.querySelectorAll(selector)).filter((el) => {
      if (el.tabIndex < 0 || el.closest('[hidden]') || el.closest('[aria-hidden="true"]')) return false;
      if (!el.getClientRects().length) return false;
      return getComputedStyle(el).visibility !== 'hidden';
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /* Called by every add-to-cart path with the /cart/add response (sections: cart-drawer, cart-icon-bubble). */
  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');
    this.productId = parsedState && parsedState.id;
    const sections = parsedState && parsedState.sections;
    if (sections && sections['cart-drawer']) this.applyDrawerHTML(sections['cart-drawer']);
    else this.refreshFromServer();
    if (sections && sections['cart-icon-bubble']) this.applyIconBubble(sections['cart-icon-bubble']);

    setTimeout(() => {
      if (!this.classList.contains('active')) this.open();
    });
  }

  applySections(sections) {
    if (!sections) {
      this.refreshFromServer();
      return;
    }
    if (sections['cart-drawer']) this.applyDrawerHTML(sections['cart-drawer']);
    if (sections['cart-icon-bubble']) this.applyIconBubble(sections['cart-icon-bubble']);
  }

  applyIconBubble(html) {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !html) return;
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (section) bubble.innerHTML = section.innerHTML;
  }

  /* Re-read the drawer from the server; concurrent calls collapse into one more fetch. */
  refreshFromServer() {
    if (this._refreshing) {
      this._refreshAgain = true;
      return this._refreshing;
    }
    this._refreshing = fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((html) => this.applyDrawerHTML(html))
      .catch((e) => console.error(e))
      .finally(() => {
        this._refreshing = null;
        if (this._refreshAgain) {
          this._refreshAgain = false;
          this.refreshFromServer();
        }
      });
    return this._refreshing;
  }

  applyDrawerHTML(html) {
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
    const cur = this.querySelector('#CartDrawer');
    if (!next || !cur) return;
    const canMorph = this.classList.contains('active') && cur.querySelector('.cd-stage') && next.querySelector('.cd-stage');
    if (canMorph) this.morph(cur, next);
    else this.replaceDrawer(cur, next);
  }

  /* Drawer closed (or old markup): a full re-render nobody sees. New rows fade in as the drawer opens. */
  replaceDrawer(cur, next) {
    const before = new Set(Array.from(cur.querySelectorAll('.cd-line')).map((row) => row.dataset.cdKey));
    cur.innerHTML = next.innerHTML;
    if (before.size) {
      cur.querySelectorAll('.cd-line').forEach((row) => {
        if (!before.has(row.dataset.cdKey)) row.classList.add('is-new');
      });
    }
    this.syncEmpty();
    this.refreshEta();
  }

  /* Drawer open: patch values in place. Nothing outside the changed row moves. */
  morph(cur, next) {
    const list = cur.querySelector('.cd-lines');
    const nextRows = Array.from(next.querySelectorAll('.cd-lines > .cd-line'));
    if (!list) return this.replaceDrawer(cur, next);

    if (this.classList.contains('is-empty') && !this._fading && nextRows.length) {
      this.fadeFromEmpty(cur, next);
      return;
    }

    // Values outside the rows: header count, meter text, footer amounts, clear-cart question.
    cur.querySelectorAll('[data-cd-slot]').forEach((el) => {
      if (el.closest('.cd-line')) return;
      const twin = next.querySelector(`[data-cd-slot="${el.dataset.cdSlot}"]`);
      if (!twin) return;
      // The savings line fades out with its old text instead of going blank first.
      const saveRow = twin.closest('[data-cd-save]');
      if (saveRow && saveRow.classList.contains('is-off')) return;
      if (el.innerHTML !== twin.innerHTML) el.innerHTML = twin.innerHTML;
    });

    const ship = cur.querySelector('[data-cd-ship]');
    const nextShip = next.querySelector('[data-cd-ship]');
    if (ship && nextShip) {
      if (ship.className !== nextShip.className) ship.className = nextShip.className;
      const fill = ship.querySelector('[data-shipping-fill]');
      const nextFill = nextShip.querySelector('[data-shipping-fill]');
      if (fill && nextFill && fill.style.width !== nextFill.style.width) {
        fill.style.width = nextFill.style.width;
        if (typeof ship.remember === 'function') ship.remember(parseFloat(nextFill.style.width) || 0);
      }
    }
    const save = cur.querySelector('[data-cd-save]');
    const nextSave = next.querySelector('[data-cd-save]');
    if (save && nextSave) save.classList.toggle('is-off', nextSave.classList.contains('is-off'));
    const checkout = cur.querySelector('[data-cd-checkout]');
    const nextCheckout = next.querySelector('[data-cd-checkout]');
    if (checkout && nextCheckout) checkout.disabled = nextCheckout.disabled;

    // Rows, matched by line key.
    const current = new Map();
    list.querySelectorAll(':scope > .cd-line').forEach((row) => current.set(row.dataset.cdKey, row));
    const seen = new Set();
    nextRows.forEach((nextRow) => {
      const key = nextRow.dataset.cdKey;
      seen.add(key);
      const row = current.get(key);
      if (row && !row.classList.contains('is-collapsing')) {
        this.patchRow(row, nextRow);
        if (row.classList.contains('is-removed')) {
          clearTimeout(row._cdTimer);
          row.classList.remove('is-removed');
        }
      } else {
        const fresh = document.importNode(nextRow, true);
        list.appendChild(fresh);
        this.expand(fresh);
      }
    });
    current.forEach((row, key) => {
      if (seen.has(key) || row.classList.contains('is-removed') || row.classList.contains('is-collapsing')) return;
      this.collapse(row);
    });

    if (!nextRows.length && !list.querySelector('.cd-line.is-removed')) this.fadeToEmpty(next);
  }

  patchRow(row, nextRow) {
    ['id', 'data-index', 'data-cd-restore', 'data-variant-id', 'aria-label'].forEach((name) => {
      const value = nextRow.getAttribute(name);
      if (value === null) row.removeAttribute(name);
      else if (row.getAttribute(name) !== value) row.setAttribute(name, value);
    });
    row.querySelectorAll('[data-cd-slot]').forEach((el) => {
      const twin = nextRow.querySelector(`[data-cd-slot="${el.dataset.cdSlot}"]`);
      if (twin && el.innerHTML !== twin.innerHTML) el.innerHTML = twin.innerHTML;
    });
    const mine = row.querySelectorAll('[data-cd-sync]');
    const theirs = nextRow.querySelectorAll('[data-cd-sync]');
    mine.forEach((el, i) => {
      const twin = theirs[i];
      if (!twin) return;
      const skip = (name) => name === 'class' || name === 'style' || name === 'value';
      Array.from(twin.attributes).forEach(({ name, value }) => {
        if (!skip(name) && el.getAttribute(name) !== value) el.setAttribute(name, value);
      });
      Array.from(el.attributes).forEach(({ name }) => {
        if (!skip(name) && !twin.hasAttribute(name)) el.removeAttribute(name);
      });
    });
    const input = row.querySelector('.quantity__input');
    const nextInput = nextRow.querySelector('.quantity__input');
    if (input && nextInput) {
      const value = nextInput.getAttribute('value');
      input.setAttribute('value', value);
      if (document.activeElement !== input) input.value = value;
    }
    const quantityInput = row.querySelector('quantity-input');
    if (quantityInput && typeof quantityInput.validateQtyRules === 'function') quantityInput.validateQtyRules();
  }

  /* A row slides in: its own height grows from 0. */
  expand(row) {
    if (cdReducedMotion()) return;
    const height = row.offsetHeight;
    row.style.transition = 'none';
    row.style.height = '0px';
    row.style.paddingTop = '0px';
    row.style.paddingBottom = '0px';
    row.style.opacity = '0';
    row.style.borderBottomColor = 'transparent';
    void row.offsetHeight;
    row.style.transition = `height ${CD_MOTION_MS}ms var(--atb-ease-out, ease), padding ${CD_MOTION_MS}ms var(--atb-ease-out, ease), opacity ${CD_MOTION_MS}ms ease, border-color ${CD_MOTION_MS}ms ease`;
    row.style.height = `${height}px`;
    row.style.paddingTop = '';
    row.style.paddingBottom = '';
    row.style.opacity = '';
    row.style.borderBottomColor = '';
    setTimeout(() => {
      row.style.transition = '';
      row.style.height = '';
    }, CD_MOTION_MS + 60);
  }

  /* A row folds away: its own height shrinks to 0, then it leaves the DOM. */
  collapse(row, done) {
    if (cdReducedMotion()) {
      row.remove();
      if (done) done();
      return;
    }
    row.style.height = `${row.offsetHeight}px`;
    void row.offsetHeight;
    row.classList.add('is-collapsing');
    row.style.height = '0px';
    let finished = false;
    const end = () => {
      if (finished) return;
      finished = true;
      row.remove();
      if (done) done();
    };
    row.addEventListener('transitionend', (event) => {
      if (event.target === row && event.propertyName === 'height') end();
    });
    setTimeout(end, CD_MOTION_MS + 140);
  }

  /* After the undo window: fold the row away, or fade to the empty view if it was the last one. */
  finalizeRemoval(row) {
    if (!row || !row.isConnected || !row.classList.contains('is-removed')) return;
    const list = row.parentElement;
    const others = Array.from(list.querySelectorAll(':scope > .cd-line')).filter(
      (other) => other !== row && !other.classList.contains('is-collapsing')
    );
    if (!others.length) {
      this.fadeToEmpty(null);
      return;
    }
    this.collapse(row);
  }

  fadeToEmpty(next) {
    if (this._fading) return;
    this._fading = true;
    const focusWasInside = !!(this.querySelector('.cd-cartview') && this.querySelector('.cd-cartview').contains(document.activeElement));
    this.classList.add('is-empty');
    const finish = (fresh) => {
      const cur = this.querySelector('#CartDrawer');
      if (fresh && cur) cur.innerHTML = fresh.innerHTML;
      this._fading = false;
      this.syncEmpty();
      this.refreshEta();
      const lost = !document.activeElement || document.activeElement === document.body || !document.activeElement.isConnected;
      if ((focusWasInside || lost) && this.classList.contains('active')) {
        const cta = this.querySelector('.cd-emptyview .cd-cta');
        if (cta) cta.focus({ preventScroll: true });
      }
    };
    setTimeout(() => {
      if (next) {
        finish(next);
        return;
      }
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((html) => finish(new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer')))
        .catch(() => finish(null));
    }, cdReducedMotion() ? 0 : CD_MOTION_MS + 40);
  }

  fadeFromEmpty(cur, next) {
    this._fading = true;
    cur.innerHTML = next.innerHTML;
    this.classList.add('is-empty');
    void this.offsetWidth;
    this._fading = false;
    this.classList.remove('is-empty');
    this.refreshEta();
  }

  /* Screen readers: one short sentence after a change the customer made. */
  announce(prefix) {
    const live = this.querySelector('#CartDrawer-LiveRegionText');
    if (!live) return;
    const text = (sel) => {
      const el = this.querySelector(sel);
      return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
    };
    const count = text('[data-cd-slot="count"]') || 'No items';
    const total = text('[data-cd-slot="total"]');
    const ship = text('[data-cd-slot="ship"]');
    live.textContent = `${prefix ? `${prefix} ` : ''}Cart updated: ${count}, estimated total ${total}. ${ship}.`;
  }

  showCartError(message) {
    const box = this.querySelector('#CartDrawer-CartErrors');
    if (!box) return;
    box.textContent = message || (window.cartStrings && window.cartStrings.error) || 'Something went wrong. Try again.';
    clearTimeout(this._errorTimer);
    this._errorTimer = setTimeout(() => {
      box.textContent = '';
    }, 4500);
  }

  /* Same rule as the product page (snippets/atb-estimated-delivery.liquid): 5 days, +1 after 2pm local time. */
  refreshEta() {
    this.querySelectorAll('[data-cd-eta]').forEach((el) => {
      try {
        const now = new Date();
        const eta = new Date(now);
        eta.setDate(eta.getDate() + 5 + (now.getHours() >= 14 ? 1 : 0));
        el.textContent = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(eta);
      } catch (e) {
        /* the server-rendered date stays */
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.addEventListener('click', (event) => this.onDrawerClick(event));
  }

  get drawer() {
    return this.closest('cart-drawer') || document.querySelector('cart-drawer');
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  sectionNames() {
    return this.getSectionsToRender().map((section) => section.section);
  }

  /* Something else changed the cart (product form, suggestions, another script): re-read and morph. */
  onCartUpdate() {
    const drawer = this.drawer;
    if (!this.isConnected || !drawer) return Promise.resolve();
    return drawer.refreshFromServer();
  }

  /* Dawn looks for #Quantity-N (the cart page); the drawer's inputs are #Drawer-quantity-N. */
  resetQuantityInput(id) {
    const input = this.querySelector(`#Drawer-quantity-${id}`);
    if (input) input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  onDrawerClick(event) {
    const undo = event.target.closest('[data-cd-undo]');
    if (undo) {
      event.preventDefault();
      this.undoLine(undo.closest('.cd-line'));
      return;
    }
    const listfoot = this.querySelector('[data-cd-listfoot]');
    if (!listfoot) return;
    if (event.target.closest('[data-cd-clear]')) {
      listfoot.classList.add('is-asking');
      setTimeout(() => {
        const keep = listfoot.querySelector('[data-cd-clear-no]');
        if (keep) keep.focus({ preventScroll: true });
      });
    } else if (event.target.closest('[data-cd-clear-no]')) {
      listfoot.classList.remove('is-asking');
      const clear = listfoot.querySelector('[data-cd-clear]');
      if (clear) clear.focus({ preventScroll: true });
    } else if (event.target.closest('[data-cd-clear-yes]')) {
      this.clearCart(listfoot);
    }
  }

  /* Dawn calls this for typed quantities, the − / + buttons and <cart-remove-button> (quantity 0). */
  updateQuantity(line, quantity, event, name, variantId) {
    const row = this.querySelector(`#CartDrawer-Item-${line}`);
    if (!row) return;
    const qty = parseInt(quantity, 10);
    if (qty === 0) {
      this.removeLine(row);
      return;
    }
    const drawer = this.drawer;
    const key = row.dataset.cdKey;
    const input = row.querySelector('.quantity__input');
    const previous = input ? input.getAttribute('value') : null;
    row.classList.remove('has-err');
    row.classList.add('is-busy');
    const body = JSON.stringify({ id: key, quantity: qty, sections: this.sectionNames(), sections_url: cdSectionsUrl() });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json().then((json) => ({ ok: response.ok, json })))
      .then(({ ok, json }) => {
        if (!ok || json.errors || json.status) {
          if (input && previous !== null) input.value = previous;
          const quantityInput = row.querySelector('quantity-input');
          if (quantityInput && typeof quantityInput.validateQtyRules === 'function') quantityInput.validateQtyRules();
          this.showLineError(row, json.errors || json.description || window.cartStrings.error);
          return;
        }
        drawer.applySections(json.sections);
        const updated = (json.items || []).find((item) => item.key === key);
        if (updated && updated.quantity !== qty) this.showLineError(row, `Only ${updated.quantity} available.`);
        drawer.announce();
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: json, variantId: variantId });
      })
      .catch(() => drawer.showCartError())
      .finally(() => row.classList.remove('is-busy'));
  }

  showLineError(row, message) {
    // Shopify says "Only 11 items were added to your cart due to availability." even when nothing changed.
    const limit = String(message || '').match(/only (\d+)/i);
    if (limit) message = `Only ${limit[1]} available.`;
    const text = row.querySelector('.cart-item__error-text');
    if (text) text.textContent = message;
    row.classList.add('has-err');
    clearTimeout(row._cdErrTimer);
    row._cdErrTimer = setTimeout(() => {
      row.classList.remove('has-err');
      if (text) text.textContent = '';
    }, 3500);
  }

  /* Remove now (the cart and totals update), but keep the row's space as "Removed X. Undo" for 5 seconds. */
  removeLine(row) {
    if (!row || row.classList.contains('is-removed')) return;
    const drawer = this.drawer;
    let restore = null;
    try {
      restore = JSON.parse(row.dataset.cdRestore || 'null');
    } catch (e) {
      restore = null;
    }
    row._cdRestore = restore;
    const key = row.dataset.cdKey;
    const title = row.querySelector('.cd-name') ? row.querySelector('.cd-name').textContent.trim() : 'Item';
    const hadFocus = row.contains(document.activeElement);
    row.classList.remove('is-busy', 'has-err');
    row.classList.add('is-removed');
    // The server renumbers the remaining lines; this row must not keep ids the others now use.
    row.removeAttribute('id');
    row.removeAttribute('data-index');
    row.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    if (hadFocus) {
      const undo = row.querySelector('[data-cd-undo]');
      if (undo) undo.focus({ preventScroll: true });
    }
    const body = JSON.stringify({ id: key, quantity: 0, sections: this.sectionNames(), sections_url: cdSectionsUrl() });
    row._cdRemoval = fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json().then((json) => ({ ok: response.ok, json })))
      .then(({ ok, json }) => {
        if (!ok || json.errors || json.status) throw new Error(json.description || json.errors || 'remove failed');
        drawer.applySections(json.sections);
        drawer.announce(`Removed ${title}. Undo is available for 5 seconds.`);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: json, variantId: restore && restore.id });
        return true;
      })
      .catch(() => {
        clearTimeout(row._cdTimer);
        row.classList.remove('is-removed');
        drawer.showCartError();
        drawer.refreshFromServer();
        return false;
      });
    clearTimeout(row._cdTimer);
    row._cdTimer = setTimeout(() => drawer.finalizeRemoval(row), CD_UNDO_MS);
  }

  /* Put the line back exactly: same variant, quantity, properties (the box's _box_* too) and selling plan. */
  undoLine(row) {
    if (!row || !row.classList.contains('is-removed') || row._cdUndoing) return;
    const drawer = this.drawer;
    const restore = row._cdRestore;
    clearTimeout(row._cdTimer);
    row._cdUndoing = true;
    row.classList.add('is-undoing');
    (row._cdRemoval || Promise.resolve(true))
      .then((removed) => {
        if (removed === false) return null;
        if (!restore || !restore.id) throw new Error('nothing to restore');
        const item = { id: restore.id, quantity: restore.quantity || 1 };
        // Liquid's `properties | json` is a list of [key, value] pairs; /cart/add wants an object.
        const props = Array.isArray(restore.properties) ? Object.fromEntries(restore.properties) : restore.properties;
        if (props && Object.keys(props).length) item.properties = props;
        if (restore.selling_plan) item.selling_plan = restore.selling_plan;
        const body = JSON.stringify({ items: [item], sections: this.sectionNames(), sections_url: cdSectionsUrl() });
        return fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.json().then((json) => ({ ok: response.ok, json })))
          .then(({ ok, json }) => {
            if (!ok || json.status) throw new Error(json.description || 'undo failed');
            const focusOnUndo = row.contains(document.activeElement) || document.activeElement === document.body;
            drawer.applySections(json.sections);
            if (row.classList.contains('is-removed')) {
              // The line came back under a different key, so it slid in as a new row; fold the old one away.
              drawer.collapse(row);
            } else {
              const remove = row.querySelector('.cd-remove');
              if (remove && focusOnUndo) remove.focus({ preventScroll: true });
            }
            drawer.announce('Item put back.');
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: json });
          });
      })
      .catch(() => {
        drawer.showCartError();
        row._cdTimer = setTimeout(() => drawer.finalizeRemoval(row), CD_UNDO_MS);
      })
      .finally(() => {
        row._cdUndoing = false;
        row.classList.remove('is-undoing');
      });
  }

  /* Asked first (the row turned into "Remove all N items?"). Empties the cart, then fades to the empty view. */
  clearCart(listfoot) {
    const drawer = this.drawer;
    const buttons = listfoot.querySelectorAll('button');
    buttons.forEach((button) => (button.disabled = true));
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${root}cart/clear.js`, { method: 'POST', headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error('clear failed');
        return response.json();
      })
      .then((cart) =>
        fetch(`${cdSectionsUrl()}?sections=cart-drawer,cart-icon-bubble`)
          .then((response) => response.json())
          .then((sections) => ({ cart, sections }))
      )
      .then(({ cart, sections }) => {
        drawer.querySelectorAll('.cd-line').forEach((row) => clearTimeout(row._cdTimer));
        drawer.applyIconBubble(sections['cart-icon-bubble']);
        const next = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html').querySelector('#CartDrawer');
        drawer.fadeToEmpty(next);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
      })
      .catch(() => {
        buttons.forEach((button) => (button.disabled = false));
        drawer.showCartError();
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
